/**
 * Demo host for <Hovercontext />. Renders a small dashboard mock so the
 * launcher pill, target highlighter, marker badges, and annotation card
 * have something realistic to operate on.
 *
 * Run with `pnpm --filter @feedbacker/playground dev` (or `pnpm dev:demo`
 * from the repo root once we wire that script). The launcher appears in
 * the bottom-right; click to expand.
 */

import { Hovercontext } from "@feedbacker/hovercontext";
import { useState } from "react";

const SAMPLE_TASKS = [
  { id: "t1", title: "Wire up onboarding email", priority: "high" as const },
  { id: "t2", title: "Fix dashboard skeleton flicker", priority: "med" as const },
  { id: "t3", title: "Add retry button to webhook failures", priority: "low" as const },
];

export const App = () => {
  const [tab, setTab] = useState<"overview" | "tasks" | "blog">("overview");

  return (
    <div className="min-h-screen">
      <header className="border-b border-slate-200 bg-white">
        <div className="mx-auto max-w-5xl px-6 h-14 flex items-center gap-4">
          <span className="font-semibold tracking-tight">Benji&rsquo;s Dashboard</span>
          <nav className="ml-auto flex items-center gap-2">
            {(["overview", "tasks", "blog"] as const).map((t) => (
              <button
                key={t}
                type="button"
                onClick={() => setTab(t)}
                className={`h-8 px-3 rounded-md text-sm transition-colors ${
                  tab === t
                    ? "bg-indigo-50 text-indigo-700 ring-1 ring-inset ring-indigo-200"
                    : "text-slate-500 hover:text-slate-900 hover:bg-slate-100"
                }`}
              >
                {t}
              </button>
            ))}
          </nav>
        </div>
      </header>

      <main className="mx-auto max-w-5xl px-6 py-10 space-y-10">
        {tab === "overview" && (
          <>
            <section>
              <h1 className="text-2xl font-semibold tracking-tight">
                Welcome back, Benji
              </h1>
              <p className="mt-2 text-slate-600">
                This page exists to give Hovercontext a realistic surface to
                annotate. Hover the launcher pill in the bottom-right corner
                and click any element below.
              </p>
            </section>

            <section className="grid grid-cols-3 gap-4">
              {[
                { label: "Sessions", value: "12" },
                { label: "Pending", value: "3" },
                { label: "Resolved", value: "27" },
              ].map((m) => (
                <article
                  key={m.label}
                  className="rounded-xl bg-white p-5 ring-1 ring-slate-200 shadow-sm"
                >
                  <div className="text-xs uppercase tracking-wider text-slate-500">
                    {m.label}
                  </div>
                  <div className="mt-1 text-2xl font-semibold tabular-nums">
                    {m.value}
                  </div>
                </article>
              ))}
            </section>

            <section className="rounded-xl bg-white p-6 ring-1 ring-slate-200">
              <div className="flex items-center justify-between">
                <h2 className="font-semibold">Quick actions</h2>
                <button
                  type="button"
                  className="upgrade-btn h-9 px-4 rounded-lg bg-indigo-600 text-white text-sm font-medium hover:bg-indigo-700"
                >
                  Upgrade plan
                </button>
              </div>
              <ul className="mt-4 grid grid-cols-2 gap-3 text-sm">
                {[
                  "Invite teammate",
                  "Connect webhook",
                  "Configure MCP server",
                  "Import existing project",
                ].map((label) => (
                  <li key={label}>
                    <button
                      type="button"
                      className="w-full h-10 rounded-md bg-slate-50 hover:bg-slate-100 ring-1 ring-slate-200 text-left px-3 text-slate-700"
                    >
                      {label}
                    </button>
                  </li>
                ))}
              </ul>
            </section>
          </>
        )}

        {tab === "tasks" && (
          <section className="rounded-xl bg-white ring-1 ring-slate-200 divide-y divide-slate-100">
            {SAMPLE_TASKS.map((t) => (
              <label
                key={t.id}
                className="flex items-center gap-3 px-5 py-3 hover:bg-slate-50 cursor-pointer"
              >
                <input type="checkbox" />
                <span className="flex-1 text-sm">{t.title}</span>
                <span
                  className={`text-[11px] uppercase tracking-wider px-2 py-0.5 rounded-full ${
                    t.priority === "high"
                      ? "bg-rose-50 text-rose-700"
                      : t.priority === "med"
                        ? "bg-amber-50 text-amber-700"
                        : "bg-emerald-50 text-emerald-700"
                  }`}
                >
                  {t.priority}
                </span>
              </label>
            ))}
          </section>
        )}

        {tab === "blog" && (
          <article className="prose prose-slate max-w-none rounded-xl bg-white p-8 ring-1 ring-slate-200">
            <h2 className="font-semibold text-xl">
              Simple can be harder than complex
            </h2>
            <p className="italic mt-3 text-slate-700">
              &ldquo;Simple can be harder than complex: You have to work hard
              to get your thinking clean to make it simpl. But it&rsquo;s worth
              it in the end because once you get there, you can move mountains.&rdquo;
            </p>
            <p className="mt-2 text-slate-500">— Steve Jobs</p>
            <p className="mt-4 text-slate-700">
              Try the Text mode: select the typo (&ldquo;simpl&rdquo;) and
              Hovercontext captures the surrounding context so an AI agent can
              fix it precisely.
            </p>
          </article>
        )}
      </main>

      <Hovercontext />
    </div>
  );
};
